import React, { useRef } from 'react';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import takeRight from 'lodash/takeRight';
import upperFirst from 'lodash/upperFirst';
import { useTranslation } from 'react-i18next';

import { useGlobalContext } from 'contexts/global-context';
import { useRouterContext } from 'contexts/router-context';
import useCustomHeaderOnScroll from 'hooks/useCustomHeaderOnScroll';
import useInnerViewportHeight from 'hooks/useInnerViewportHeight';
import useThrottleInstallGameClick from 'hooks/useThrottleInstallGameClick';
import Header from 'components/header';
import Video from 'components/video';
import Game from 'components/game';
import Section from 'components/section';
import LookALikeGames from 'components/look-a-like-games';
import PokiGenreGames from 'components/poki-genre-games';
import Loader from 'components/loader';
import Modal from 'components/modal';
import ScrollChecker from 'components/scroll-checker';
import { Genre, HomePageSection, Route, TGame } from 'types';
import { closeWebView } from 'utils';
import GamePage from './game';
import GenresPage from './genres';

const HOME_PAGE_SECTIONS_ORDER = [
  HomePageSection.moreGamesLikeThis,
  HomePageSection.gameOfDay,
  HomePageSection.genre1,
  HomePageSection.genre2,
  HomePageSection.genre3,
  HomePageSection.genre4,
  HomePageSection.genre5,
  HomePageSection.genre6,
  HomePageSection.genre7,
];

const Home = () => {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const { currentRoute, history, back } = useRouterContext();
  const { games, gameOfDay, lookALikeGames, loading } = useGlobalContext();

  useInnerViewportHeight();
  useCustomHeaderOnScroll(null, null, headerRef);

  const onGameOfDayClick = useThrottleInstallGameClick(gameOfDay);
  const groupedGames = groupBy(games, (game: TGame) => game.genre);

  return (
    <>
      {loading && <Loader />}
      <Header innerRef={headerRef} hideBackBtn onClick={closeWebView} />
      {!isEmpty(lookALikeGames) && (
        <ScrollChecker name={HomePageSection.moreGamesLikeThis} sectionOrder={HOME_PAGE_SECTIONS_ORDER}>
          <LookALikeGames games={lookALikeGames} />
        </ScrollChecker>
      )}
      {gameOfDay && (
        <ScrollChecker name={HomePageSection.gameOfDay} sectionOrder={HOME_PAGE_SECTIONS_ORDER}>
          <Section title={t('gameOfDay')}>
            {gameOfDay.videoShort && (
              <Video
                muted
                autoRepeat
                autoPlayInView
                hideVolumeControls
                withControls={false}
                url={gameOfDay.videoShort}
                poster={gameOfDay.videoPoster}
                handleClick={onGameOfDayClick}
              />
            )}
            <Game compact aboveParent game={gameOfDay} onClick={onGameOfDayClick} />
          </Section>
        </ScrollChecker>
      )}
      {Object.keys(groupedGames).map((genre: string, index: number) => {
        return (
          <ScrollChecker
            key={genre}
            name={HomePageSection[`genre${index + 1}` as keyof typeof HomePageSection]}
            sectionOrder={HOME_PAGE_SECTIONS_ORDER}
          >
            <Section title={t(`genre${upperFirst(genre)}`)}>
              <PokiGenreGames genre={genre as Genre} games={groupedGames[genre]} genreOrder={index} />
            </Section>
          </ScrollChecker>
        );
      })}
      {[currentRoute, ...takeRight(history)].filter((route) => route.name !== Route.home).map((route) => {
        if (route?.name === Route.game) {
          const gameId = route?.param || 1;
          const game = games.find((game) => game.id === gameId) as TGame;

          return (
            <Modal
              key={`${route.name}-${route.param}`}
              isCurrent={currentRoute.param === route.param}
              renderChildren={(parentRef, closeWithAnimation) => (
                <GamePage game={game} parentRef={parentRef} closeWithAnimation={closeWithAnimation} />
              )}
              onClose={back}
            />
          );
        } else {
          return (
            <Modal
              key={`${route.name}-${route.param}`}
              isCurrent={currentRoute.name === Route.genres}
              renderChildren={(parentRef, closeWithAnimation) => (
                <GenresPage parentRef={parentRef} closeWithAnimation={closeWithAnimation} />
              )}
              onClose={back}
            />
          );
        }
      })}
    </>
  );
}

export default Home;
